import { useNavigate, useSearchParams } from 'react-router';
import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { linkedinApi } from '@/lib/api/linkedin';
import { googleApi } from '@/lib/api/google';

import {
  CompanySignUpForm,
  SignupFormValues,
} from '@/forms/SignupForm/CompanySignUpForm';
import { AuthFormShell } from '@/components/PageHeader';
import { AuthLayout } from '@/layouts/AuthLayout';
import { useTranslation } from 'react-i18next';
import {
  Step,
  StepComponent,
  StepNavigation,
} from '@/components/StepNavigation';
import { Step01AccountType } from './signup/Step01AccountType';
import { parseAsInteger, useQueryState } from 'nuqs';
import { AccountType, type LinkedinPendingSignupDto } from '@paladin-monorepo/common';

export type SsoSignupProvider = 'linkedin' | 'google';

export interface SignupState {
  accountType?: AccountType;
  userDetails?: SignupFormValues;
  linkedinPending?: LinkedinPendingSignupDto;
  linkedinPathActive?: boolean;
  googlePending?: LinkedinPendingSignupDto;
  googlePathActive?: boolean;
  ssoProvider?: SsoSignupProvider;
}

export default function SignupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [state, setState] = useState<SignupState>({});

  const [searchParams] = useSearchParams();
  const refParam = searchParams.get('ref');
  const linkedinRef = refParam === 'linkedin';
  const googleRef = refParam === 'google';
  const ssoHydrated = useRef(false);

  useEffect(() => {
    if ((!linkedinRef && !googleRef) || ssoHydrated.current) {
      return;
    }
    ssoHydrated.current = true;

    const api = linkedinRef ? linkedinApi : googleApi;
    const expiredKey = linkedinRef
      ? 'auth.linkedin.errors.pendingExpired'
      : 'auth.google.errors.pendingExpired';

    api
      .getPendingSignup()
      .then((pending) => {
        setState((prev) => ({
          ...prev,
          ...(linkedinRef
            ? { linkedinPending: pending, linkedinPathActive: true, ssoProvider: 'linkedin' as const }
            : { googlePending: pending, googlePathActive: true, ssoProvider: 'google' as const }),
          userDetails: {
            ...(prev.userDetails ?? {}),
            fullName: pending.fullName,
            email: pending.email,
            company: pending.employerName ?? prev.userDetails?.company ?? '',
          } as SignupFormValues,
        }));
      })
      .catch((error) => {
        if (axios.isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 401)) {
          toast.error(t(expiredKey));
        }
      });
  }, [linkedinRef, googleRef, t]);

  const [step, setStep] = useQueryState(
    'step',
    parseAsInteger.withDefault(0).withOptions({
      shallow: false,
      clearOnDefault: true,
    })
  );

  const steps: Step[] = [
    {
      id: 'accountType',
      labelKey: 'signup.steps.accountType',
      description: '',
      component: Step01AccountType,
    },
    {
      id: 'userDetails',
      labelKey: 'signup.steps.userDetails',
      description: '',
      component: CompanySignUpForm,
      prerequisite: () => !!state.accountType,
      props: {
        onSuccess: () => {
          sessionStorage.setItem('signup_completed', '1');
          navigate('/auth/signup/thank-you', {
            state: { signupCompleted: true },
          });
        },
      },
    },
  ];

  const validStep = Math.max(0, Math.min(step, steps.length - 1));

  const nextStep = async () => {
    setCompletedSteps((prev) => new Set([...prev, validStep]));
    if (validStep < steps.length - 1) {
      const newStep = validStep + 1;
      setStep(newStep);
    }
  };

  const prevStep = () => {
    if (validStep > 0) {
      const newStep = validStep - 1;
      setStep(newStep);
    }
  };

  const goToStep = (stepIndex: number) => {
    if (stepIndex <= validStep || completedSteps.has(stepIndex - 1)) {
      setStep(stepIndex);
    }
  };

  const CurrentStep = steps[validStep].component as React.ComponentType<
    StepComponent & any
  >;

  if (steps[validStep].prerequisite && !steps[validStep].prerequisite()) {
    setStep(validStep - 1);
    return;
  }

  return (
    <AuthLayout contentWidth="wide">
      <AuthFormShell
        title={t('signup.title')}
        description={
          validStep === 0 ? t('signup.descriptionAccountType') : t('signup.description')
        }
      >
        <div className="flex w-full flex-col items-center justify-center gap-8">
          <div className="w-full max-w-[320px]">
            <StepNavigation
              variant="bullet"
              steps={steps}
              currentStep={validStep}
              completedSteps={completedSteps}
              onStepClick={(stepIndex) => {
                goToStep(stepIndex);
              }}
            />
          </div>

          <CurrentStep
            onBack={prevStep}
            onContinue={nextStep}
            state={state}
            setState={setState}
            {...steps[validStep].props}
          />
        </div>
      </AuthFormShell>
    </AuthLayout>
  );
}
